import type { LayerSpecification } from "maplibre-gl";
import { SOURCES } from "../sources";
import { LAYERS } from "./constants";

// All primitive features live in a single GeoJSON source. Styling is split
// across layers because of two MapLibre constraints:
//   • line-dasharray is NOT data-driven — it can't vary per feature, so each
//     dash style (solid/dashed/dotted) needs its own line layer, routed by the
//     feature's `lineStyle` property via the layer `filter`.
//   • line-gradient can't be set per-feature and can't coexist with a dash
//     array — so gradients are faked upstream in PrimitivesManager by emitting
//     colored sub-segments rendered through the solid line layer.
// color / opacity / width DO support data-driven expressions, so those are read
// straight off each feature's properties below.

// Shared, per-feature stroke paint reused by all three line layers.
const linePaint = {
  "line-color": ["coalesce", ["get", "color"], "#F8F8A2"],
  "line-opacity": ["coalesce", ["get", "opacity"], 1],
  "line-width": ["coalesce", ["get", "width"], 2],
} as const;

// Shape fills. Line layers below also render Polygon ring outlines, so a shape
// gets both a fill (here) and an outline (whichever line layer its lineStyle
// routes it to).
export const primitivesFill: LayerSpecification = {
  id: LAYERS.PRIMITIVES_FILL,
  type: "fill",
  source: SOURCES.PRIMITIVES,
  filter: ["==", ["geometry-type"], "Polygon"],
  paint: {
    "fill-color": ["coalesce", ["get", "fillColor"], "rgba(141,135,219,0.4)"],
    "fill-opacity": ["coalesce", ["get", "fillOpacity"], 0.3],
  },
};

export const primitivesLineSolid: LayerSpecification = {
  id: LAYERS.PRIMITIVES_LINE_SOLID,
  type: "line",
  source: SOURCES.PRIMITIVES,
  filter: ["==", ["get", "lineStyle"], "solid"],
  layout: { "line-join": "round", "line-cap": "round" },
  paint: { ...linePaint },
};

export const primitivesLineDashed: LayerSpecification = {
  id: LAYERS.PRIMITIVES_LINE_DASHED,
  type: "line",
  source: SOURCES.PRIMITIVES,
  filter: ["==", ["get", "lineStyle"], "dashed"],
  layout: { "line-join": "round", "line-cap": "butt" },
  // dasharray units are multiples of line-width: 3 on, 2 off.
  paint: { ...linePaint, "line-dasharray": [3, 2] },
};

export const primitivesLineDotted: LayerSpecification = {
  id: LAYERS.PRIMITIVES_LINE_DOTTED,
  type: "line",
  source: SOURCES.PRIMITIVES,
  filter: ["==", ["get", "lineStyle"], "dotted"],
  // round cap on a short on-segment renders as a dot.
  layout: { "line-join": "round", "line-cap": "round" },
  paint: { ...linePaint, "line-dasharray": [0.1, 2] },
};

// Fill first (bottom), then strokes on top.
export const primitivesLayers: LayerSpecification[] = [
  primitivesFill,
  primitivesLineSolid,
  primitivesLineDashed,
  primitivesLineDotted,
];
